<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import SpacesAPI from 'dashboard/api/spaces';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import ItemDialog from './ItemDialog.vue';
import PriceSuggestionChip from './PriceSuggestionChip.vue';
import { ATTRIBUTE_CATEGORIES } from '../attributes/constants';
import {
  normalizeSuggestionBands,
  normalizeTier,
  priceMatchesSuggestion,
} from './pricingTiers';

const store = useStore();
const { t } = useI18n();
const useAlertMessage = useAlert;
const { currentAccount } = useAccount();

const defaultTaxRate = computed(
  () => currentAccount.value?.default_tax_rate ?? null
);

const searchQuery = ref('');
const filterMode = ref('all');
const categoryFilter = ref('all');
const itemDialogRef = ref(null);
const activeRow = ref(null);
const activePrice = ref(null);
const sortField = ref('name');
const sortDirection = ref('asc');
const collapsedItems = ref(new Set());

const loading = computed(
  () => store.getters['pricingItems/getUIFlags'].isFetching
);
const pricingItems = computed(
  () => store.getters['pricingItems/getRecords'] || []
);
const spaces = computed(() => store.getters['spaces/getSpaces'] || []);
const eventAttributes = computed(
  () =>
    store.getters['attributes/getAttributesByModel']?.('event_attribute') || []
);

const mergedRows = computed(() => {
  const rows = [];
  const itemsBySpaceId = new Map();
  const itemsByAttrKey = new Map();

  pricingItems.value.forEach(pi => {
    if (pi.space_id) itemsBySpaceId.set(pi.space_id, pi);
    else if (pi.attribute_key) itemsByAttrKey.set(pi.attribute_key, pi);
  });

  spaces.value.forEach(space => {
    const pi = itemsBySpaceId.get(space.id);
    const meta = space.metadata || {};
    const tier = normalizeTier(space.tier || meta.tier);
    const hasCapacity = [
      space.max_capacity_standing,
      space.max_capacity_seated,
      space.max_capacity_seated_furn,
    ].some(val => val != null && val !== '' && val !== 'n/a' && Number(val) > 0);
    const rawPrice = space.suggested_price ?? meta.suggested_price;
    const suggestionReady =
      hasCapacity &&
      tier &&
      tier !== 'none' &&
      rawPrice != null &&
      rawPrice !== '';
    const suggestion = suggestionReady
      ? {
          price: rawPrice,
          low: space.suggested_price_low ?? meta.suggested_price_low,
          high: space.suggested_price_high ?? meta.suggested_price_high,
          currency:
            space.suggested_price_currency ||
            meta.suggested_price_currency ||
            'EUR',
          unit:
            space.suggested_price_unit ||
            meta.suggested_price_unit ||
            'per_hour',
          bands: space.suggested_price_bands || meta.suggested_price_bands || [],
          dayHours: space.suggested_day_hours ?? meta.suggested_day_hours,
        }
      : null;
    rows.push({
      key: `space:${space.id}`,
      type: 'space',
      name: space.name,
      icon: 'i-lucide-orbit',
      configured: !!pi,
      pricingItem: pi || null,
      prices: pi?.prices || [],
      tier: tier && tier !== 'none' ? tier : null,
      suggestion,
      sourcePayload: { space_id: space.id, category: 'space' },
    });
  });

  eventAttributes.value.forEach(attr => {
    const pi = itemsByAttrKey.get(attr.attribute_key);
    rows.push({
      key: `attr:${attr.attribute_key}`,
      type: 'attribute',
      name: attr.attribute_display_name,
      icon: 'i-lucide-code',
      configured: !!pi,
      pricingItem: pi || null,
      prices: pi?.prices || [],
      attributeCategory: attr.attribute_category || null,
      customAttributeCategoryId: attr.custom_attribute_category_id || null,
      sourcePayload: { attribute_key: attr.attribute_key, category: 'other' },
    });
  });

  // Include pricing items linked to discarded/removed attributes
  const knownAttrKeys = new Set(
    eventAttributes.value.map(a => a.attribute_key)
  );
  itemsByAttrKey.forEach((pi, attrKey) => {
    if (!knownAttrKeys.has(attrKey)) {
      rows.push({
        key: `attr:${attrKey}`,
        type: 'attribute',
        name: pi.name || attrKey,
        icon: 'i-lucide-ban',
        configured: true,
        unlinked: true,
        pricingItem: pi,
        prices: pi.prices || [],
        sourcePayload: { attribute_key: attrKey, category: 'other' },
      });
    }
  });

  return rows;
});

const configurableItemOptions = computed(() =>
  mergedRows.value.filter(row => !row.configured && !row.unlinked)
);

const customAttributeCategories = computed(
  () => store.getters['customAttributeCategories/getRecords'] || []
);

const categoryFilterOptions = computed(() => {
  const options = [
    { value: 'all', label: t('PRICING.MASTER_SHEET.FILTER_ALL') },
  ];
  options.push({
    value: 'space',
    label: t('PRICING.MASTER_SHEET.CATEGORY_SPACE'),
  });
  ATTRIBUTE_CATEGORIES.forEach(cat => {
    const catValue = cat.key.toLowerCase();
    options.push({
      value: catValue,
      label: t(`ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.${cat.key}`),
    });
    const subCats = customAttributeCategories.value.filter(
      c => c.parent_category === catValue && c.active
    );
    subCats.forEach(sub => {
      options.push({
        value: `sub:${sub.id}`,
        label: `  └ ${sub.name}`,
        isSubCategory: true,
      });
    });
  });
  return options;
});

const filteredRows = computed(() => {
  let result = mergedRows.value;

  if (filterMode.value === 'archived') {
    result = result.filter(r => r.unlinked);
  } else {
    result = result.filter(r => !r.unlinked);
    if (filterMode.value === 'configured') {
      result = result.filter(r => r.configured);
    } else if (filterMode.value === 'not_configured') {
      result = result.filter(r => !r.configured);
    }
  }

  if (categoryFilter.value !== 'all') {
    if (categoryFilter.value === 'space') {
      result = result.filter(r => r.type === 'space');
    } else if (categoryFilter.value.startsWith('sub:')) {
      const subId = parseInt(categoryFilter.value.replace('sub:', ''), 10);
      result = result.filter(r => r.customAttributeCategoryId === subId);
    } else {
      result = result.filter(r => r.attributeCategory === categoryFilter.value);
    }
  }

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    result = result.filter(r => r.name.toLowerCase().includes(q));
  }

  const dir = sortDirection.value === 'asc' ? 1 : -1;
  return [...result].sort((a, b) => {
    if (a.configured !== b.configured) return a.configured ? -1 : 1;
    return String(a.name).localeCompare(String(b.name)) * dir;
  });
});

const toggleSort = field => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
};

const sortIcon = field => {
  if (sortField.value !== field) return 'i-lucide-arrow-up-down';
  return sortDirection.value === 'asc'
    ? 'i-lucide-arrow-up'
    : 'i-lucide-arrow-down';
};

const toggleItem = key => {
  const next = new Set(collapsedItems.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedItems.value = next;
};

onMounted(async () => {
  store.dispatch('attributes/get');
  store.dispatch('customAttributeCategories/get');
  await Promise.all([
    store.dispatch('pricingItems/get'),
    store.dispatch('spaces/get'),
  ]);
  backfillMissingSuggestionBands();
});

const spaceHasSuggestionBands = space => {
  const bands =
    space?.suggested_price_bands || space?.metadata?.suggested_price_bands;
  return normalizeSuggestionBands(bands).length > 0;
};

const spaceCanSuggest = space => {
  const tier = normalizeTier(space?.tier || space?.metadata?.tier);
  if (!tier || tier === 'none') return false;
  return [
    space.max_capacity_standing,
    space.max_capacity_seated,
    space.max_capacity_seated_furn,
  ].some(val => val != null && val !== '' && val !== 'n/a' && Number(val) > 0);
};

const backfillMissingSuggestionBands = async () => {
  const spacesNeedingBands = (spaces.value || []).filter(
    space => spaceCanSuggest(space) && !spaceHasSuggestionBands(space)
  );
  if (!spacesNeedingBands.length) return;

  await Promise.all(
    spacesNeedingBands.map(async space => {
      try {
        const { data } = await SpacesAPI.refreshSuggestedPrice(space.id);
        if (data?.id) {
          store.commit('spaces/EDIT_SPACE', data);
        }
      } catch {
        // Soft-fail: capacity missing or Moneytron unavailable
      }
    })
  );
};

const openNewRate = () => {
  activeRow.value = {
    key: 'new',
    type: 'standalone',
    name: '',
    configured: false,
    pricingItem: null,
    prices: [],
    sourcePayload: { category: 'other' },
  };
  activePrice.value = null;
  itemDialogRef.value?.open();
};

const openConfigure = row => {
  activeRow.value = row;
  activePrice.value = null;
  itemDialogRef.value?.open();
};

const openAddPrice = row => {
  activeRow.value = row;
  activePrice.value = null;
  itemDialogRef.value?.open();
};

const openEditPrice = (row, price) => {
  activeRow.value = row;
  activePrice.value = price;
  itemDialogRef.value?.open();
};

const handleSave = async data => {
  try {
    const spaceTier = activeRow.value?.tier || null;
    const prices = Array.isArray(data.prices_attributes)
      ? data.prices_attributes.map(price => ({
          ...price,
          tier: price.tier || spaceTier || 'none',
        }))
      : data.prices_attributes;
    const dataWithTier =
      prices != null ? { ...data, prices_attributes: prices } : data;
    const payload =
      activeRow.value.type === 'standalone'
        ? dataWithTier
        : {
            ...activeRow.value.sourcePayload,
            name: activeRow.value.name,
            ...dataWithTier,
          };
    if (activeRow.value.pricingItem?.id) {
      await store.dispatch('pricingItems/update', {
        id: activeRow.value.pricingItem.id,
        ...payload,
      });
      useAlertMessage(t('PRICING.MASTER_SHEET.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('pricingItems/create', payload);
      store.dispatch('pricingPlans/get');
      useAlertMessage(t('PRICING.MASTER_SHEET.CREATE_SUCCESS'));
    }
    itemDialogRef.value?.close();
    if (activeRow.value?.key) {
      const next = new Set(collapsedItems.value);
      next.add(activeRow.value.key);
      collapsedItems.value = next;
    }
  } catch {
    useAlertMessage(t('PRICING.MASTER_SHEET.SAVE_ERROR'));
  }
};

const handleDeletePrice = async (row, price) => {
  if (!row.pricingItem?.id || !price?.id) return;
  try {
    await store.dispatch('pricingItems/update', {
      id: row.pricingItem.id,
      prices_attributes: [{ id: price.id, _destroy: true }],
    });
    useAlertMessage(t('PRICING.MASTER_SHEET.DELETE_PRICE_SUCCESS'));
  } catch {
    useAlertMessage(t('PRICING.MASTER_SHEET.DELETE_PRICE_ERROR'));
  }
};

const setDefaultPrice = async (row, price) => {
  if (!row.pricingItem?.id || !price?.id || price.is_default) return;
  try {
    await store.dispatch('pricingItems/update', {
      id: row.pricingItem.id,
      prices_attributes: [{ id: price.id, is_default: true }],
    });
    useAlertMessage(t('PRICING.MASTER_SHEET.SET_DEFAULT_SUCCESS'));
  } catch {
    useAlertMessage(t('PRICING.MASTER_SHEET.SET_DEFAULT_ERROR'));
  }
};

const handleRemoveConfig = async row => {
  if (!row.pricingItem?.id) return;
  try {
    await store.dispatch('pricingItems/delete', row.pricingItem.id);
    useAlertMessage(t('PRICING.MASTER_SHEET.REMOVE_SUCCESS'));
  } catch {
    useAlertMessage(t('PRICING.MASTER_SHEET.REMOVE_ERROR'));
  }
};

const formatUnit = unit =>
  t(`PRICING.UNITS.${(unit || '').toUpperCase()}`) || unit;

const EMPTY_DISPLAY = '—';

const defaultPriceFor = row =>
  row.prices?.find(p => p.is_default) || row.prices?.[0] || null;

const bandPlaceholdersFor = row => {
  if (row.type !== 'space' || !row.suggestion) return [];
  const unit = row.suggestion.unit || 'per_hour';
  const currency = (row.suggestion.currency || 'EUR').toUpperCase();
  const levels = [
    {
      key: 'low',
      labelKey: 'PRICING.BAND_LEVELS.LOW',
      price: row.suggestion.low,
    },
    {
      key: 'mid',
      labelKey: 'PRICING.BAND_LEVELS.MID',
      price: row.suggestion.price,
    },
    {
      key: 'high',
      labelKey: 'PRICING.BAND_LEVELS.HIGH',
      price: row.suggestion.high,
    },
  ];

  return levels.filter(level => {
    if (level.price == null || level.price === '') return false;
    const covered = (row.prices || []).some(
      price =>
        price.unit === unit &&
        (price.currency || 'EUR').toUpperCase() === currency &&
        priceMatchesSuggestion(price.price, level.price)
    );
    return !covered;
  });
};

const rowHasExpandableContent = row =>
  (row.prices?.length || 0) > 0 || bandPlaceholdersFor(row).length > 0;

const applyBandSuggestion = async (row, level) => {
  if (!row || level?.price == null) return;

  const priceAttrs = {
    tier: row.tier || 'none',
    name: t(level.labelKey),
    unit: row.suggestion?.unit || 'per_hour',
    currency: row.suggestion?.currency || 'EUR',
    price: Number(level.price),
    is_default: !(row.prices?.length),
  };

  try {
    if (row.pricingItem?.id) {
      await store.dispatch('pricingItems/update', {
        id: row.pricingItem.id,
        prices_attributes: [priceAttrs],
      });
      useAlertMessage(t('PRICING.MASTER_SHEET.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('pricingItems/create', {
        ...row.sourcePayload,
        name: row.name,
        prices_attributes: [priceAttrs],
      });
      store.dispatch('pricingPlans/get');
      useAlertMessage(t('PRICING.MASTER_SHEET.CREATE_SUCCESS'));
    }
  } catch {
    useAlertMessage(t('PRICING.MASTER_SHEET.SAVE_ERROR'));
  }
};

const filterOptions = [
  { key: 'all', label: t('PRICING.MASTER_SHEET.FILTER_ALL') },
  { key: 'configured', label: t('PRICING.MASTER_SHEET.FILTER_CONFIGURED') },
  {
    key: 'not_configured',
    label: t('PRICING.MASTER_SHEET.FILTER_NOT_CONFIGURED'),
  },
  { key: 'archived', label: t('PRICING.MASTER_SHEET.FILTER_ARCHIVED') },
];

const showFilterModeDropdown = ref(false);
const showCategoryDropdown = ref(false);

const currentFilterModeLabel = computed(
  () =>
    filterOptions.find(option => option.key === filterMode.value)?.label ||
    t('PRICING.MASTER_SHEET.FILTER_ALL')
);

const currentCategoryLabel = computed(
  () =>
    categoryFilterOptions.value.find(
      option => option.value === categoryFilter.value
    )?.label || t('PRICING.MASTER_SHEET.FILTER_ALL')
);

const selectFilterMode = key => {
  filterMode.value = key;
  showFilterModeDropdown.value = false;
};

const selectCategory = value => {
  categoryFilter.value = value;
  showCategoryDropdown.value = false;
};

const closeFilterDropdowns = () => {
  showFilterModeDropdown.value = false;
  showCategoryDropdown.value = false;
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('PRICING.LOADING')"
    :no-records-found="!loading && mergedRows.length === 0"
    :no-records-message="t('PRICING.MASTER_SHEET.EMPTY')"
  >
    <template #header>
      <div class="flex flex-col w-full gap-4">
        <BaseSettingsHeader
          :title="t('PRICING.MASTER_SHEET.TITLE')"
          :description="t('PRICING.MASTER_SHEET.DESCRIPTION')"
          description-as-info-tooltip
        >
          <template #actions>
            <Button
              icon="i-lucide-plus"
              md
              :label="t('PRICING.MASTER_SHEET.ADD_RATE')"
              @click="openNewRate"
            />
          </template>
        </BaseSettingsHeader>

        <div class="flex items-center gap-2 w-full">
          <div class="relative shrink-0">
            <Button
              md
              slate
              outlined
              :aria-label="t('PRICING.MASTER_SHEET.FILTER_ALL')"
              :aria-expanded="showFilterModeDropdown"
              aria-haspopup="listbox"
              @click="showFilterModeDropdown = !showFilterModeDropdown"
            >
              <template #icon>
                <Icon icon="i-lucide-filter" class="size-5 shrink-0" />
              </template>
              <span class="flex items-center gap-1.5 min-w-0">
                <span class="truncate font-semibold">{{
                  currentFilterModeLabel
                }}</span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 shrink-0 text-slate-400"
                />
              </span>
            </Button>
            <div
              v-if="showFilterModeDropdown"
              class="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50"
            >
              <div class="py-1">
                <button
                  v-for="opt in filterOptions"
                  :key="opt.key"
                  type="button"
                  class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  :class="{
                    'text-woot-600 dark:text-woot-400 font-medium bg-woot-50 dark:bg-woot-900/20':
                      filterMode === opt.key,
                    'text-slate-700 dark:text-slate-300':
                      filterMode !== opt.key,
                  }"
                  @click="selectFilterMode(opt.key)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="relative shrink-0">
            <Button
              md
              slate
              outlined
              :aria-label="t('PRICING.MASTER_SHEET.FILTER_ALL')"
              :aria-expanded="showCategoryDropdown"
              aria-haspopup="listbox"
              @click="showCategoryDropdown = !showCategoryDropdown"
            >
              <template #icon>
                <Icon icon="i-lucide-tags" class="size-5 shrink-0" />
              </template>
              <span class="flex items-center gap-1.5 min-w-0">
                <span class="truncate font-semibold max-w-48">{{
                  currentCategoryLabel
                }}</span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 shrink-0 text-slate-400"
                />
              </span>
            </Button>
            <div
              v-if="showCategoryDropdown"
              class="absolute top-full left-0 mt-2 w-64 max-h-80 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50"
            >
              <div class="py-1">
                <button
                  v-for="opt in categoryFilterOptions"
                  :key="opt.value"
                  type="button"
                  class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                  :class="{
                    'text-woot-600 dark:text-woot-400 font-medium bg-woot-50 dark:bg-woot-900/20':
                      categoryFilter === opt.value,
                    'text-slate-700 dark:text-slate-300':
                      categoryFilter !== opt.value,
                    'pl-6': opt.isSubCategory,
                  }"
                  @click="selectCategory(opt.value)"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="relative flex-1 min-w-[12rem]">
            <span
              class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none z-[1]"
            />
            <input
              v-model="searchQuery"
              type="search"
              class="w-full h-10 !pl-9 !pr-9 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 !mb-0 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              :placeholder="t('PRICING.MASTER_SHEET.SEARCH_PLACEHOLDER')"
            />
            <button
              v-if="searchQuery"
              type="button"
              class="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              :aria-label="t('PRICING.MASTER_SHEET.SEARCH_CLEAR')"
              @click="searchQuery = ''"
            >
              <Icon icon="i-lucide-x" class="size-4 align-middle" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr class="border-b border-n-weak text-sm">
            <th
              class="py-2 ltr:text-left rtl:text-right font-semibold text-n-slate-11 cursor-pointer select-none w-1/3"
              @click="toggleSort('name')"
            >
              <span class="inline-flex items-center gap-1">
                {{ t('PRICING.TABLE.ITEM_NAME') }}
                <Icon :icon="sortIcon('name')" class="size-3.5" />
              </span>
            </th>
            <th
              class="py-2 ltr:text-right rtl:text-left ltr:pr-4 rtl:pl-4 font-semibold text-n-slate-11"
            >
              {{ t('PRICING.ITEM_DIALOG.PRICE_LABEL') }}
            </th>
            <th
              class="py-2 ltr:text-left rtl:text-right ltr:pr-4 rtl:pl-4 font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.CURRENCY') }}
            </th>
            <th
              class="py-2 ltr:text-left rtl:text-right ltr:pr-4 rtl:pl-4 font-semibold text-n-slate-11"
            >
              {{ t('PRICING.ITEM_DIALOG.UNIT_LABEL') }}
            </th>
            <th
              class="py-2 ltr:text-right rtl:text-left font-semibold text-n-slate-11"
            >
              {{ t('PRICING.TABLE.TAX_RATE') }}
            </th>
            <th
              class="py-2 ltr:text-right rtl:text-left font-semibold text-n-slate-11 w-40"
            >
              {{ t('PRICING.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <template v-for="row in filteredRows" :key="row.key">
            <!-- Parent row: pricing item -->
            <tr
              class="border-b border-n-weak !border-t-2 !border-t-strong"
              :class="[
                { 'opacity-50': !row.configured },
                rowHasExpandableContent(row)
                  ? 'cursor-pointer select-none'
                  : '',
              ]"
              @click="
                rowHasExpandableContent(row) ? toggleItem(row.key) : null
              "
            >
              <td class="py-2">
                <div class="flex items-center gap-2">
                  <span
                    class="w-5 h-5 flex items-center justify-center shrink-0"
                  >
                    <template v-if="rowHasExpandableContent(row)">
                      <svg
                        class="size-3.5 transition-transform"
                        :class="collapsedItems.has(row.key) ? '' : 'rotate-90'"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </template>
                  </span>
                  <Icon :icon="row.icon" class="size-5 text-n-slate-10" />
                  <span
                    class="font-semibold text-neutral-900 dark:text-white truncate"
                    >{{ row.name }}</span>
                  <span
                    v-if="row.prices.length > 1"
                    class="text-xs text-n-slate-10"
                  >
                    {{ row.prices.length }}
                  </span>
                  <span
                    v-if="!row.configured"
                    class="rounded-full bg-n-alpha-1 px-2 py-0.5 text-xs text-n-slate-10"
                  >
                    {{ t('PRICING.MASTER_SHEET.NOT_CONFIGURED') }}
                  </span>
                  <span
                    v-if="row.unlinked"
                    class="rounded-full bg-n-ruby-3 px-2 py-0.5 text-xs text-n-ruby-11 truncate"
                  >
                    {{ t('PRICING.MASTER_SHEET.UNLINKED') }}
                  </span>
                </div>
              </td>
              <td class="py-2 pr-4 text-right font-medium">
                <template v-if="defaultPriceFor(row)">
                  <PriceSuggestionChip
                    :price="defaultPriceFor(row)"
                    :suggestion="row.suggestion"
                  />
                </template>
                <template v-else>{{ EMPTY_DISPLAY }}</template>
              </td>
              <td class="py-2 pr-4 text-n-slate-10">
                <span
                  v-if="defaultPriceFor(row)"
                  class="text-xs text-n-slate-10"
                >
                  {{ defaultPriceFor(row).currency }}
                </span>
              </td>
              <td class="py-2 pr-4 text-n-slate-10">
                <template v-if="defaultPriceFor(row)">
                  <span class="text-xs text-n-slate-10">
                    {{ formatUnit(defaultPriceFor(row).unit) }}
                  </span>
                </template>
                <template v-else>{{ EMPTY_DISPLAY }}</template>
              </td>
              <td class="py-2 text-right text-n-slate-10">
                <template v-if="defaultPriceFor(row)?.tax_rate != null">
                  {{ defaultPriceFor(row).tax_rate }}%
                </template>
                <template v-else-if="defaultTaxRate != null">
                  <span class="text-n-slate-9">{{ defaultTaxRate }}%</span>
                </template>
                <template v-else>—</template>
              </td>
              <td class="py-2">
                <div class="flex items-center justify-end gap-2">
                  <Button
                    v-if="row.configured"
                    v-tooltip.top="t('PRICING.MASTER_SHEET.ADD_PRICE')"
                    icon="i-lucide-plus"
                    md
                    slate
                    faded
                    @click.stop="openAddPrice(row)"
                  />
                  <Button
                    v-if="row.configured"
                    v-tooltip.top="t('PRICING.MASTER_SHEET.REMOVE_CONFIG')"
                    icon="i-lucide-trash-2"
                    md
                    ruby
                    faded
                    @click.stop="handleRemoveConfig(row)"
                  />
                  <Button
                    v-if="!row.configured"
                    v-tooltip.top="t('PRICING.MASTER_SHEET.CONFIGURE')"
                    icon="i-lucide-settings-2"
                    md
                    slate
                    faded
                    @click.stop="openConfigure(row)"
                  />
                </div>
              </td>
            </tr>

            <!-- Child rows: individual prices + band suggestion placeholders -->
            <template
              v-if="
                !collapsedItems.has(row.key) && rowHasExpandableContent(row)
              "
            >
              <tr
                v-for="price in row.prices"
                :key="price.id"
                class="border-b border-n-weak/50 bg-n-solid-1"
              >
                <td class="py-2">
                  <div class="flex items-center gap-2 pl-[28px]">
                    <span
                      v-if="price.name"
                      class="text-sm font-medium text-n-slate-12"
                    >
                      {{ price.name }}
                    </span>
                    <button
                      v-if="row.prices.length > 1"
                      class="text-xs font-medium px-2 py-0.5 rounded-full transition-colors"
                      :class="
                        price.is_default
                          ? 'bg-n-brand/75 text-white cursor-default'
                          : 'bg-n-alpha-1 text-n-slate-10 hover:bg-n-brand/10 hover:text-n-brand'
                      "
                      @click.stop="setDefaultPrice(row, price)"
                    >
                      {{ t('PRICING.ITEM_DIALOG.DEFAULT_LABEL') }}
                    </button>
                  </div>
                </td>
                <td class="py-2 pr-4 text-right font-medium">
                  <PriceSuggestionChip
                    :price="price"
                    :suggestion="row.suggestion"
                  />
                </td>
                <td class="py-2 pr-4">
                  <span class="text-xs text-n-slate-10">
                    {{ price.currency }}
                  </span>
                </td>
                <td class="py-2 pr-4">
                  <span class="text-xs text-n-slate-10">
                    {{ formatUnit(price.unit) }}
                  </span>
                </td>
                <td class="py-2 text-right">
                  <template v-if="price.tax_rate != null">
                    {{ price.tax_rate }}%
                  </template>
                  <template v-else-if="defaultTaxRate != null">
                    <span class="text-n-slate-9">{{ defaultTaxRate }}%</span>
                  </template>
                  <template v-else>—</template>
                </td>
                <td class="py-2">
                  <div class="flex items-center justify-end gap-2">
                    <Button
                      v-tooltip.top="t('PRICING.EDIT')"
                      icon="i-lucide-pen-line"
                      md
                      slate
                      faded
                      @click="openEditPrice(row, price)"
                    />
                    <Button
                      v-if="row.prices.length > 1"
                      v-tooltip.top="t('PRICING.DELETE')"
                      icon="i-lucide-trash-2"
                      md
                      ruby
                      faded
                      @click="handleDeletePrice(row, price)"
                    />
                  </div>
                </td>
              </tr>
              <tr
                v-for="level in bandPlaceholdersFor(row)"
                :key="`${row.key}:suggest:${level.key}`"
                class="border-b border-n-weak/50 bg-n-solid-1/60 border-dashed"
              >
                <td class="py-2">
                  <div class="flex items-center gap-2 pl-[28px]">
                    <span
                      class="rounded-full border border-dashed border-n-weak px-2 py-0.5 text-xs text-n-slate-10"
                    >
                      {{ t('PRICING.MASTER_SHEET.SUGGESTION_PLACEHOLDER') }}
                    </span>
                    <span class="text-sm text-n-slate-11">
                      {{ t(level.labelKey) }}
                    </span>
                  </div>
                </td>
                <td class="py-2 pr-4 text-right font-medium">
                  <span class="text-n-slate-9 opacity-70">{{ level.price }}</span>
                </td>
                <td class="py-2 pr-4">
                  <span class="text-xs text-n-slate-9 opacity-70">
                    {{ row.suggestion.currency }}
                  </span>
                </td>
                <td class="py-2 pr-4">
                  <span class="text-xs text-n-slate-9 opacity-70">
                    {{ formatUnit(row.suggestion.unit) }}
                  </span>
                </td>
                <td class="py-2 text-right text-n-slate-9">—</td>
                <td class="py-2">
                  <div class="flex items-center justify-end gap-2">
                    <Button
                      v-tooltip.top="t('PRICING.MASTER_SHEET.APPLY_SUGGESTION')"
                      icon="i-lucide-sparkles"
                      md
                      slate
                      faded
                      @click.stop="applyBandSuggestion(row, level)"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <ItemDialog
    ref="itemDialogRef"
    :row="activeRow"
    :price="activePrice"
    :item-options="configurableItemOptions"
    @save="handleSave"
    @close="activeRow = null"
  />

  <div
    v-if="showFilterModeDropdown || showCategoryDropdown"
    class="fixed inset-0 z-40"
    @click="closeFilterDropdowns"
  />
</template>
